import { Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { NativeBootstrap } from './components/native-bootstrap'
import { ActiveSessionBanner } from './components/active-session-banner'
import { LocalCacheLifecycle } from './components/local-cache-lifecycle'
import { LiveJourneyPingController } from './features/sessions/components/live-journey-ping-controller'
import { SessionCallRecorderController } from './features/sessions/components/session-call-recorder-controller'
import { SessionRecordingController } from './features/session-recording/components/session-recording-controller'
import { VideoCallOverlay } from './components/video-call-overlay'
import { VoiceCallOverlay } from './components/voice-call-overlay'
import { GroupCallOverlay } from './components/group-call-overlay'
import { ReturnToCallBanner } from './components/return-to-call-banner'
import { ActiveElsewhereBanner } from './components/active-elsewhere-banner'
import { CallGateDialog } from './components/call-gate-dialog'
import { RecordingConsentOverlay } from './features/calls/components/recording-consent-overlay'
import { RecordingPolicyGateDialog } from './features/calls/components/recording-policy-gate-dialog'
import { RecordingConsentPeerDialog } from './features/calls/components/recording-consent-peer-dialog'
import { ConsentCallEndedDialog } from './features/calls/components/consent-call-ended-dialog'
import { SetupHost } from './features/setup/components/setup-host'
import { CallWaitingPrompt } from './components/call-waiting-prompt'
import { ProtectedRoute } from './components/protected-route'
import { RequireSessionsAccess } from './components/require-sessions-access'
import { RouteFallback } from './components/route-fallback'
import { LazyRouteBoundary } from './components/lazy-route-boundary'
import { lazyPage } from './components/lazy-page'
import { useIsAppLocked } from './stores/app-lock-store'
import { shouldBlockAppChromeForSoftLock } from './utils/call-overlay-layers'
import {
  appChromeGateProps,
  useSoftLockDomRelease,
} from './features/security/soft-lock-dom-release'
// The auth entry points stay eager: a signed-out user must never wait on a
// second chunk (or see a spinner) just to reach the sign-in form, and the
// OAuth callback has to be ready the instant the browser lands on it.
import { SignInPage } from './pages/sign-in'
import { AuthCallbackPage } from './pages/auth-callback'

const BrunoPage = lazyPage(() => import('./pages/bruno'), 'BrunoPage')
const MessagesPage = lazyPage(() => import('./pages/messages'), 'MessagesPage')
const NewGroupPage = lazyPage(() => import('./pages/new-group-page'), 'NewGroupPage')
const MenuPage = lazyPage(() => import('./pages/menu'), 'MenuPage')
const HomePage = lazyPage(() => import('./pages/home'), 'HomePage')
const AnnouncementsWallPage = lazyPage(
  () => import('./pages/announcements-wall'),
  'AnnouncementsWallPage',
)
const ForumPage = lazyPage(() => import('./pages/forum'), 'ForumPage')
const ForumReviewPage = lazyPage(() => import('./pages/forum-review'), 'ForumReviewPage')
const ProfilePage = lazyPage(() => import('./pages/profile'), 'ProfilePage')
const AdminPage = lazyPage(() => import('./pages/admin'), 'AdminPage')
const LoginSecurityPage = lazyPage(
  () => import('./pages/account/login-security'),
  'LoginSecurityPage',
)
const DisplayAccessibilityPage = lazyPage(
  () => import('./pages/account/display-accessibility'),
  'DisplayAccessibilityPage',
)
const ActivityStatusPage = lazyPage(
  () => import('./pages/account/activity-status'),
  'ActivityStatusPage',
)
const AvailabilityPage = lazyPage(() => import('./pages/account/availability'), 'AvailabilityPage')
const NotificationSettingsPage = lazyPage(
  () => import('./pages/account/notification-settings'),
  'NotificationSettingsPage',
)
const DialToneSettingsPage = lazyPage(
  () => import('./pages/account/dial-tone-settings'),
  'DialToneSettingsPage',
)
const FamilyAccessPage = lazyPage(() => import('./pages/account/family-access'), 'FamilyAccessPage')
const RecordingPolicyPage = lazyPage(
  () => import('./pages/account/recording-policy'),
  'RecordingPolicyPage',
)
const ManageDevicesPage = lazyPage(
  () => import('./pages/account/manage-devices'),
  'ManageDevicesPage',
)
const CallsPage = lazyPage(() => import('./pages/calls'), 'CallsPage')
const SessionsPage = lazyPage(() => import('./features/sessions/sessions-page'), 'SessionsPage')
const SessionRecordingReviewPage = lazyPage(
  () => import('./pages/session-recording-review'),
  'SessionRecordingReviewPage',
)
const RecordingConsentPage = lazyPage(() => import('./pages/recording-consent'), 'RecordingConsentPage')
const NotificationsPage = lazyPage(() => import('./pages/notifications'), 'NotificationsPage')
const PrivacyPolicyPage = lazyPage(() => import('./pages/privacy-policy'), 'PrivacyPolicyPage')
const TermsPage = lazyPage(() => import('./pages/terms'), 'TermsPage')
const BugReportsPage = lazyPage(() => import('./pages/bug-reports-page'), 'BugReportsPage')
const OnCallSettingsPage = lazyPage(
  () => import('./pages/on-call-settings-page'),
  'OnCallSettingsPage',
)
const CallDiagnosticsPage = lazyPage(() => import('./pages/call-diagnostics'), 'CallDiagnosticsPage')
// DEV-only mocked preview of the member journey strip + provider trace map
// (/dev/journey-preview). Dead code in production builds — the import is
// behind the compile-time DEV flag so no chunk is emitted.
const JourneyPreviewPage = import.meta.env.DEV
  ? lazyPage(() => import('./pages/dev/journey-preview'), 'JourneyPreviewPage')
  : null
// DEV / opt-in (VITE_SAFE_AREA_PREVIEW=true) native-chrome preview with no
// sign-in, for checking iOS safe areas on a simulator (/dev/safe-area-preview).
const SafeAreaPreviewPage =
  import.meta.env.DEV || import.meta.env.VITE_SAFE_AREA_PREVIEW === 'true'
    ? lazyPage(() => import('./pages/dev/safe-area-preview'), 'SafeAreaPreviewPage')
    : null
// HIDDEN Phase 0 native-recorder test (/dev/session-recorder-test): shipped in
// signed test builds but gated at runtime to the Provider Owner + native shells
// (see features/session-recorder-test/utils/recorder-gate.ts). Never uploads audio.
const SessionRecorderTestPage = lazyPage(
  () => import('./pages/dev/session-recorder-test'),
  'SessionRecorderTestPage',
)
const DeleteAccountPage = lazyPage(() => import('./pages/delete-account'), 'DeleteAccountPage')

/**
 * Redirect a legacy `/messages/:channel` path onto the unified `/messages`
 * route while preserving the query string (e.g. `?thread=…` deep links).
 */
function MessagesRedirect() {
  const { search } = useLocation()
  return <Navigate to={`/messages${search}`} replace />
}

export default function App() {
  const locked = useIsAppLocked()
  // Locked means gated — even mid-call (RC audit product decision). The call
  // surfaces below render outside this container, so they stay interactive.
  const blockAppChrome = shouldBlockAppChromeForSoftLock(locked)
  // Belt-and-braces: on every locked→unlocked transition, sweep any lock DOM
  // artifacts a teardown path missed (and log loudly in dev if any existed).
  useSoftLockDomRelease()

  return (
    <>
    <NativeBootstrap />
    {/* Per-user screen cache: wipe on sign-out, purge on account switch, prefetch Sessions. */}
    <LocalCacheLifecycle />
    {/* Catches a rejected chunk import(), which Suspense alone would leave
        stuck on the loading splash forever. Soft-lock makes this subtree
        inert so lists behind the sheet are not tappable, scrollable, or
        focusable (single contract: appChromeGateProps ↔ the unlock release
        sweep). The gate holds during a live call too — call overlays, the
        return-to-call banner, and the call prompts stay outside this div so
        Answer/Switch/End and in-call chat keep working while locked. */}
    <div {...appChromeGateProps(blockAppChrome)}>
    <LazyRouteBoundary>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          {/* Dev-only mocked preview (no auth: it renders fixtures, not data). */}
          {JourneyPreviewPage && (
            <Route path="/dev/journey-preview" element={<JourneyPreviewPage />} />
          )}
          {SafeAreaPreviewPage && (
            <Route path="/dev/safe-area-preview" element={<SafeAreaPreviewPage />} />
          )}
          <Route element={<ProtectedRoute />}>
            {/* Keeps the query: `/?setup=<step>` (push tap / Alerts row) opens setup. */}
            <Route path="/" element={<MessagesRedirect />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/menu" element={<MenuPage />} />
            {/* Unified messages — direct + group conversations live under a single route. */}
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/messages/new" element={<NewGroupPage />} />
            {/* Legacy aliases redirect into the unified messages surface. */}
            <Route path="/messages/direct" element={<MessagesRedirect />} />
            <Route path="/messages/groups" element={<MessagesRedirect />} />
            <Route path="/messages/groups/new" element={<Navigate to="/messages/new" replace />} />
            <Route path="/messages/announcements" element={<Navigate to="/announcements/all" replace />} />
            <Route path="/announcements" element={<Navigate to="/announcements/all" replace />} />
            {/* Owner/provider moderator queue — ranked above the :channel route. */}
            <Route path="/announcements/review" element={<ForumReviewPage />} />
            <Route path="/announcements/:channel" element={<AnnouncementsWallPage />} />
            {/* Member Forum — single-channel, moderated re-skin for non-provider users. */}
            <Route path="/forum" element={<ForumPage />} />
            <Route path="/calls" element={<CallsPage />} />
            {/* Sessions is for providers + members — guarded, not just hidden from nav. */}
            <Route element={<RequireSessionsAccess />}>
              <Route path="/sessions" element={<SessionsPage />} />
              <Route
                path="/sessions/:sessionId/recordings/:recordingId"
                element={<SessionRecordingReviewPage />}
              />
            </Route>
            {/* Session-recording consent asked in the app: for the person asked (often not a Sessions user). */}
            <Route path="/recording-consent/:sessionUuid/:requestId" element={<RecordingConsentPage />} />
            {/* /video merged into the unified Calls hub. */}
            <Route path="/video" element={<Navigate to="/calls" replace />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            {/* Account settings sub-pages (reached from Menu > Account). */}
            <Route path="/account/login" element={<LoginSecurityPage />} />
            <Route path="/account/devices" element={<ManageDevicesPage />} />
            <Route path="/account/display" element={<DisplayAccessibilityPage />} />
            <Route path="/account/activity" element={<ActivityStatusPage />} />
            <Route path="/account/availability" element={<AvailabilityPage />} />
            <Route path="/account/notifications" element={<NotificationSettingsPage />} />
            <Route path="/account/dial-tones" element={<DialToneSettingsPage />} />
            <Route path="/account/family" element={<FamilyAccessPage />} />
            <Route path="/account/recording-policy" element={<RecordingPolicyPage />} />
            <Route path="/on-call" element={<OnCallSettingsPage />} />
            {/* /admin/users/:userId keeps AdminPage mounted (list + filters survive opening a user). */}
            <Route path="/admin" element={<AdminPage />}>
              <Route path="users/:userId" element={null} />
            </Route>
            <Route path="/dev/session-recorder-test" element={<SessionRecorderTestPage />} />
            <Route path="/bug-reports" element={<BugReportsPage />} />
            <Route path="/bruno" element={<BrunoPage />} />
            <Route path="/call-diagnostics" element={<CallDiagnosticsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/messages" replace />} />
        </Routes>
      </Suspense>
    </LazyRouteBoundary>
    {/* Active-session bar lives INSIDE the gated chrome: soft-lock must block
        navigating back to the session, and it stays below the lock sheet
        (z-45 < z-50) and all call surfaces (z-60+). */}
    <ActiveSessionBanner />
    {/* Whole-session recording: REC bar on every screen while capturing. */}
    <SessionRecordingController />
    {/* Live en-route journey pings live HERE (not in the session-detail card)
        so they keep flowing on every screen and across app-background. */}
    <LiveJourneyPingController />
    </div>
    <VoiceCallOverlay />
    <VideoCallOverlay />
    <GroupCallOverlay />
    <ReturnToCallBanner />
    <ActiveElsewhereBanner />
    <CallWaitingPrompt />
    <CallGateDialog />
    <ConsentCallEndedDialog />
    <SetupHost />
    <RecordingPolicyGateDialog />
    <RecordingConsentPeerDialog />
    <RecordingConsentOverlay />
    {/* Telehealth session call → session start/end (+ Undo). Outside the gate
        like the call surfaces: its Undo must stay reachable mid-call. */}
    <SessionCallRecorderController />
    </>
  )
}
